'use client';

import { Facebook, Instagram, LinkedIn, Public, Twitter, YouTube } from '@mui/icons-material';
import { Box, Container, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { UploadFile } from '@/types';

import { FooterLinks } from './FooterLinks';

type SocialItem = { id?: number; Platform?: string; Url?: string };
type LogoItem = { id?: number; Alt?: string; Url?: string | null; Image?: UploadFile | null };

const SOCIAL_ICONS: Record<string, React.ComponentType> = {
  Facebook,
  LinkedIn,
  Instagram,
  Twitter,
  YouTube,
};

const FooterBox = styled(Box)(({ theme: _theme }) => ({
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  a: { color: 'white' },
}));

export default function Footer({
  footerMenu,
  footerCompanyMenu,
}: {
  footerMenu?: any;
  footerCompanyMenu?: any;
}) {
  const theme = useTheme();
  const { siteSettings } = useSiteSettings();

  const socials = ((siteSettings as any)?.Socials ?? []) as SocialItem[];
  const awards = ((siteSettings as any)?.Awards ?? []) as LogoItem[];
  const certificationLogos = ((siteSettings as any)?.CertificationLogos ?? []) as LogoItem[];
  const socialsHeading = ((siteSettings as any)?.SocialsHeading || '') as string;
  const awardsHeading = ((siteSettings as any)?.AwardsHeading || '') as string;
  const certificationLogosHeading = ((siteSettings as any)?.CertificationLogosHeading ||
    '') as string;
  const certificationLogosText = ((siteSettings as any)?.CertificationLogosText || '') as string;
  const certificationLogosTopSpacing = Math.min(
    80,
    Math.max(0, Number((siteSettings as any)?.CertificationLogosTopSpacing ?? 40) || 0)
  );
  const trustBadge = ((siteSettings as any)?.TrustBadge || '') as string;
  const trustHeading = ((siteSettings as any)?.TrustHeading || '') as string;
  const termsText = ((siteSettings as any)?.TermsText ?? []) as BlocksContent;

  const hasBrandStrip = awards.length > 0 || certificationLogos.length > 0;
  const hasTrustSection = Boolean(trustBadge || trustHeading || termsText.length > 0);

  const [activeAwardIdx, setActiveAwardIdx] = useState<number | null>(null);

  return (
    <footer data-component="Footer">
      <FooterBox
        sx={{
          // Intentional pure black: the theme has no #000 token
          // (palette.common.black is brand dark #200B42).
          background: '#000',
          py: 3,
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            paddingInline: {
              xl: 0,
            },
          }}
        >
          {/* Top section: Company, Legal, Trust logos */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                xl: 'repeat(7, 1fr)',
              },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-end', md: 'flex-start' },
              gap: 5,
              rowGap: 0,
              '> .company': {
                paddingInline: { xs: 2, md: 0 },
                gridRow: 1,
                mt: `calc(${theme.spacing(1)} * 0)`,
                gridColumnStart: {
                  xs: '1',
                  xl: '6',
                },
                '.MuiAccordion-root': {
                  paddingInline: 0,
                },
              },
              '> .connect': {
                paddingInline: { xs: 2, md: 0 },
                gridRow: { xl: 1 },
                gridColumnStart: {
                  xs: '1',
                  xl: '7',
                },
              },
            }}
          >
            {footerCompanyMenu && (
              <FooterLinks footerMenu={footerCompanyMenu} showLabel className="company" />
            )}
            {footerMenu && <FooterLinks footerMenu={footerMenu} />}
            {socials.length > 0 && (
              <Box className="connect" data-component="footer-connect">
                <Typography
                  fontWeight="bold"
                  sx={{
                    display: 'inline-block',
                    color: 'purple.200',
                    opacity: '0.5',
                    mb: 2,
                    mt: 3,
                    fontSize: theme.typography.h5.fontSize,
                  }}
                >
                  {socialsHeading}
                </Typography>
                <Stack direction="column" spacing={1.5} alignItems="flex-start">
                  {socials.map((social, idx) => {
                    if (!social.Url || !social.Platform) return null;
                    const Icon = SOCIAL_ICONS[social.Platform] ?? Public;
                    return (
                      <IconButton
                        key={social.id ?? idx}
                        component={Link}
                        href={social.Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.Platform}
                        size="small"
                        sx={{
                          color: 'white',
                          padding: 0.5,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            opacity: 0.7,
                          },
                        }}
                      >
                        <Icon />
                      </IconButton>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>

          {hasTrustSection && (
            <Box
              sx={{
                mt: { xs: 5, md: 6 },
                pt: { xs: 4, md: 5 },
                borderTop: '1px solid rgba(255,255,255,0.12)',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 3, md: 8 },
                alignItems: 'flex-start',
              }}
            >
              <Box>
                {trustBadge && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      backgroundColor: theme.palette.purple?.[100] ?? 'rgba(179,170,223,0.2)',
                      color: theme.palette.purple?.[700] ?? '#6B5BD6',
                      fontWeight: 600,
                      fontSize: theme.typography.pxToRem(13),
                      px: 2,
                      py: 0.75,
                      borderRadius: 999,
                      mb: 2.5,
                    }}
                  >
                    {trustBadge}
                  </Box>
                )}
                {trustHeading && (
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: {
                        xs: theme.typography.pxToRem(24),
                        md: theme.typography.pxToRem(32),
                      },
                      lineHeight: 1.2,
                    }}
                  >
                    {trustHeading}
                  </Typography>
                )}
              </Box>
              {termsText.length > 0 && (
                <Box
                  sx={{
                    color: 'white',
                    '& p': {
                      color: 'white',
                      fontSize: theme.typography.pxToRem(14),
                      lineHeight: 1.6,
                      mb: 1.5,
                    },
                    '& a': { color: 'white', textDecoration: 'underline' },
                  }}
                >
                  <GlobalBlockRenderer content={termsText} textColor="white" />
                </Box>
              )}
            </Box>
          )}

          {hasBrandStrip && (
            <Box
              sx={{
                mt: { xs: 5, md: 6 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 4, md: 5 },
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                pb: 4,
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                '& .brand-section-heading': {
                  display: 'block',
                  mb: 2,
                  fontWeight: 700,
                  fontSize: theme.typography.pxToRem(11),
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: theme.palette.purple?.[200] ?? 'rgba(255,255,255,0.85)',
                },
              }}
            >
              {/* Awards — overlapping stack, click brings to front */}
              {awards.length > 0 && (
                <Box>
                  {awardsHeading && (
                    <Typography className="brand-section-heading">{awardsHeading}</Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pl: 1,
                      pt: 1,
                      pb: 1,
                    }}
                  >
                    {awards.map((item, idx) => {
                      const src = getImageUrl({ image: item.Image, size: 'url' });
                      if (!src) return null;
                      const isActive = activeAwardIdx === idx;
                      const someoneActive = activeAwardIdx !== null;
                      const img = (
                        <Image
                          src={src}
                          alt={item.Alt || item.Image?.alternativeText || ''}
                          width={item.Image?.width ?? 80}
                          height={item.Image?.height ?? 80}
                          style={{
                            height: 76,
                            width: 'auto',
                            maxWidth: 140,
                            objectFit: 'contain',
                            display: 'block',
                          }}
                          unoptimized
                        />
                      );
                      const itemSx = {
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        ml: idx === 0 ? 0 : -3.5,
                        zIndex: isActive ? 50 : idx + 1,
                        cursor: 'pointer',
                        transition:
                          'transform 0.25s ease, filter 0.25s ease, box-shadow 0.25s ease',
                        transform: isActive ? 'translateY(-6px) scale(1.06)' : 'none',
                        filter:
                          someoneActive && !isActive ? 'brightness(0.6) saturate(0.8)' : 'none',
                        borderRadius: '50%',
                        boxShadow: isActive ? '0 10px 24px rgba(0,0,0,0.3)' : 'none',
                        '&:hover': {
                          transform: isActive ? 'translateY(-6px) scale(1.06)' : 'translateY(-3px)',
                          zIndex: isActive ? 50 : 40,
                        },
                        '&:focus-visible': {
                          outline: '2px solid white',
                          outlineOffset: 2,
                        },
                      } as const;

                      const handleActivate = () => setActiveAwardIdx(isActive ? null : idx);

                      return item.Url ? (
                        <Box
                          key={item.id ?? idx}
                          component={Link}
                          href={item.Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleActivate}
                          sx={itemSx}
                        >
                          {img}
                        </Box>
                      ) : (
                        <Box
                          key={item.id ?? idx}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          onClick={handleActivate}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleActivate();
                            }
                          }}
                          sx={itemSx}
                        >
                          {img}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Certification logos */}
              {certificationLogos.length > 0 && (
                <Box
                  sx={{
                    ml: { xs: 0, md: 'auto' },
                    mt: { xs: 0, md: `${certificationLogosTopSpacing}px` },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 3 },
                  }}
                >
                  {(certificationLogosHeading || certificationLogosText) && (
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
                      {certificationLogosHeading && (
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.65)',
                            fontSize: theme.typography.pxToRem(14),
                            lineHeight: 1.35,
                          }}
                        >
                          {certificationLogosHeading}
                        </Typography>
                      )}
                      {certificationLogosText && (
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 700,
                            fontSize: theme.typography.pxToRem(14),
                            lineHeight: 1.35,
                          }}
                        >
                          {certificationLogosText}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ alignItems: 'center' }}
                  >
                    {certificationLogos.map((item, idx) => {
                      const src = getImageUrl({ image: item.Image, size: 'url' });
                      if (!src) return null;
                      const img = (
                        <Image
                          src={src}
                          alt={item.Alt || item.Image?.alternativeText || ''}
                          width={item.Image?.width ?? 80}
                          height={item.Image?.height ?? 80}
                          style={{
                            height: 76,
                            width: 'auto',
                            maxWidth: 140,
                            objectFit: 'contain',
                          }}
                          unoptimized
                        />
                      );
                      return item.Url ? (
                        <Link
                          key={item.id ?? idx}
                          href={item.Url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {img}
                        </Link>
                      ) : (
                        <Box key={item.id ?? idx}>{img}</Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginTop: theme.spacing(hasBrandStrip ? 3 : 6),
            }}
          >
            <Link href="/" className="logo-link">
              <Image src={'/assets/logo-white.svg'} alt="Lend Logo" width={110} height={22} />
            </Link>
            <Typography
              variant="small"
              color="purple.200"
              fontWeight="bold"
              textAlign="center"
              suppressHydrationWarning
            >
              Copyright &copy; {new Date().getFullYear()}. All rights reserved. Lend Capital Pty
              Ltd, ACN 612 877 442.
            </Typography>
          </Box>
        </Container>
      </FooterBox>
    </footer>
  );
}
